import { Component } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { ClrDatagridModule, ClrDatagridSortOrder, ClrIconModule, ClrModalModule } from "@clr/angular";
import { DatePipe, NgIf } from "@angular/common";
import { UploadComponent } from "../../../../core/upload/upload.component";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { AlertService, ComponentsStoreService, FileStoreService, IAlertType } from '../../../../_shared/services';
import { Components, Files } from '../../../../_shared/interfaces';
import { TranslocoDirective } from "@jsverse/transloco";
import { getTenantStorage } from '../../../../_shared/utils/tenant-context';

import { environment as env } from "../../../../../environments/environment";
import { filesBaseUrl } from "../../../../_shared/utils/rest-url";
import {ClarityIcons, downloadIcon, timesCircleIcon} from "@cds/core/icon";

@Component({
  selector: 'app-project-details-documents',
  standalone: true,
    imports: [
        RouterModule,
        ClrDatagridModule,
        DatePipe,
        ClrIconModule,
        NgIf,
        ClrModalModule,
        UploadComponent,
        NgxExtendedPdfViewerModule,
        TranslocoDirective
    ],
  templateUrl: './project-details-documents.component.html',
  styleUrl: './project-details-documents.component.sass'
})
export class ProjectDetailsDocumentsComponent {
  projectId: string = '';
  descSortA = ClrDatagridSortOrder.DESC;
  descSortB = ClrDatagridSortOrder.DESC;
  components: Components[] = [];
  projectsFiles: Files[] = [];
  componentsFiles: Files[] = [];
  selectedFiles: Files[] = [];
  public loading: boolean = true;
  public showPdf = false;
  public showFileUpload = false;
  modalContent: string = '';
  modalUrl: string = '';

  private filesDirectory = filesBaseUrl();

  constructor(
      private componentsService: ComponentsStoreService,
      private filess: FileStoreService,
      private alertDispatcher: AlertService,
      private route: ActivatedRoute,
      )
  {
      ClarityIcons.addIcons(timesCircleIcon, downloadIcon)
  }

  ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('cid') + '.' + params.get('prid');
    this.getFiles([1,2,3,4,5,6,7,8,9,10,20,30,40,50,51,55,60,61,70,80,98,99], this.projectId);
    this.getComponentFiles(this.projectId);
  }

  showModal(fileid: string) {
    const phmToken = getTenantStorage('access_token');
    this.modalUrl = `${this.filesDirectory}/download_file?file_id=${fileid}&token=${phmToken}`;
    this.showPdf = true;
  }

  closeModal(state: boolean) {
    this.showFileUpload = state;
    this.loading = true;
    this.getFiles([1,2,3,4,5,6,7,8,9,10,20,30,40,50,51,55,60,61,70,80,98,99], this.projectId);
  }

  showFileUploader() {
    this.showFileUpload = true;
  }

  async downloadZIP(selectedFiles: Files[]) {
    if (!selectedFiles.length || !confirm(`Wollen Sie die ${selectedFiles.length} Datei(en) als ZIP herunterladen?`)) {
      return;
    }

    const zip = new JSZip();
    const phmToken = getTenantStorage('access_token');
    const url = `${this.filesDirectory}/download_file?token=${phmToken}`;

    for (const file of selectedFiles) {
      try {
        const response = await fetch(`${url}&file_id=${file.id}`);
        const blob = await response.blob();
        zip.file(file.fileName, blob);
      } catch (error) {
        this.alertDispatcher.alertConfig.message = `Fehler beim Laden von Datei: ${file.fileName}`;
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `project-${this.projectId}-files.zip`);
    this.alertDispatcher.alertConfig.message = 'ZIP-Download abgeschlossen!';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
  }

  private getFiles(doctype: number[], projectID: string) {
    this.componentsService.getComponentsFile(doctype, projectID, '0', 0, 0)
        .subscribe({
          next: (data) => {
              if (!data || !data.object || data.object.length === 0) {
                  this.projectsFiles = [];
                  this.alertDispatcher.alertConfig.message = 'Keine Dateien gefunden!';
                  this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
                  return;
              }
            this.projectsFiles = data.object;
            this.descSortA = ClrDatagridSortOrder.DESC;
          },
          complete: () => {
            this.loading = false;
          },
          error: () => {
            return;
          }
        });
  }

  private getComponentFiles(projectID: string) {
    this.filess.getComponentsFilesByProject(projectID)
        .subscribe({
          next: (data) => {
              if (!data || !data.object || data.object.length === 0) {
                  this.componentsFiles = [];
                  this.alertDispatcher.alertConfig.message = 'Keine Kommentare gefunden!';
                  this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
                  return;
              }
            this.componentsFiles = data.object;
            this.descSortB = ClrDatagridSortOrder.DESC;
          },
          complete: () => {
            this.loading = false;
          },
          error: () => {
            return;
          }
        });
  }

  deleteFile(fileID: string, fileName: string) {
    if (confirm('Wollen Sie die Datei ' + fileName + ' wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.filess.deleteFile(fileID)
          .subscribe({
            next: () => {
              this.projectsFiles = this.projectsFiles.filter(file => file.id !== fileID);
              this.componentsFiles = this.componentsFiles.filter(file => file.id !== fileID);
              this.selectedFiles = this.selectedFiles.filter(file => file.id !== fileID);
              this.alertDispatcher.alertConfig.message = 'Datei ' + fileName + ' wurde gelöscht. ';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;
            },
            complete: () => {
              this.getFiles([1,2,3,4,5,6,7,8,9,10,20,30,40,50,51,55,60,61,70,80,98,99], this.projectId);
              this.getComponentFiles(this.projectId);
            },
            error: () => {
              return
            }
          })
    }
  }

  loadFile(fileID: string, fileName: string) {
    if (confirm('Wollen Sie die Datei ' + fileName + ' herunterladen?')) {
      this.filess.downloadFile(fileID)
          .subscribe({
            next: data => {
                if (!data || data.size === 0) {
                    this.alertDispatcher.alertConfig.message = 'Keine Dateien gefunden!';
                    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
                    return;
                }
              saveAs(data, fileName);
            },
            complete: () => {
              this.alertDispatcher.alertConfig.message = 'Datei ' + fileName + ' wurde heruntergeladen. ';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
            },
            error: () => {
              return
            }
          })
    }
  }
}
